import React, { forwardRef, useState, useEffect, useId } from 'react';
import PropTypes from 'prop-types';
import {
  StyledInput,
  InputWrapper,
  InputRow,
  InputLabel,
  ErrorMessage,
  InputContainer,
  DefaultText,
} from './styled';

// Helper function to format zip code (###-####)
const formatZipCode = (value) => {
  if (!value) return '';

  // Remove all non-digit characters
  const numbers = value.replace(/\D/g, '');

  // Format as ###-####
  if (numbers.length <= 3) {
    return numbers;
  }
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}`;
};

const Input = forwardRef(
  (
    {
      label,
      subLabel,
      title,
      required,
      rules = [],
      $autoTrim = true,
      widthError,
      placeholder,
      value,
      format,
      onChange,
      widthInput,
      autoComplete,
      classNameRow,
      heightInput,
      isPopup = true,
      $maxWidthLabel,
      classNameLabel,
      widthLabel,
      className,
      onError,
      onErrorChange,
      gapLabelInput,
      classNameInput,
      onValidationChange,
      isSubmitted,
      $paddingMessage,
      defaultText,
      errors,
      ...props
    },
    ref
  ) => {
    const reactId = useId();
    const safeTitle = title
      ? String(title)
          .trim()
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-_]/g, '')
      : '';
    const inputId = props.id || props.name || safeTitle || reactId;
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const componentValidateOnChange = Array.isArray(props.validateTrigger)
      ? props.validateTrigger.includes('onChange')
      : props.validateTrigger === 'onChange';
    const anyRuleValidateOnChange = Array.isArray(rules)
      ? rules.some((r) =>
          Array.isArray(r?.validateTrigger)
            ? r.validateTrigger.includes('onChange')
            : r?.validateTrigger === 'onChange'
        )
      : false;

    useEffect(() => {
      const errorForThisInput = errors?.find(
        (err) => err.label === title && err.message !== null
      );
      if (errorForThisInput) {
        setError(true);
        setErrorMessage(errorForThisInput.message);
      } else {
        setError(false);
        setErrorMessage('');
      }
    }, [errors, title]);

    const allRules = [...(required ? [] : []), ...rules];

    const validateValue = async (val, trigger = 'onChange', options = {}) => {
      const { skipRequired = false } = options;
      const normalizedValue = val
        ? $autoTrim
          ? val.toString().trim()
          : val.toString()
        : '';

      for (const rule of allRules) {
        const ruleValidateOnChange = Array.isArray(rule.validateTrigger)
          ? rule.validateTrigger.includes('onChange')
          : rule.validateTrigger === 'onChange';
        const shouldValidateNowBase =
          trigger === 'onSubmit' ||
          componentValidateOnChange ||
          ruleValidateOnChange;
        // Required should validate onChange if ANY rule or component requests onChange
        const shouldValidateNow =
          shouldValidateNowBase ||
          (rule.required &&
            (componentValidateOnChange || anyRuleValidateOnChange));

        // Required validation
        if (
          shouldValidateNow &&
          rule.required &&
          !normalizedValue &&
          // Skip required error onChange if first char is space
          !(trigger === 'onChange' && skipRequired)
        ) {
          setError(true);
          setErrorMessage(rule.message);
          onErrorChange && onErrorChange(title, rule.message);
          onValidationChange && onValidationChange(false, rule.message);
          return false;
        }

        // Custom validator (run even when empty if requested by trigger; let validator decide)
        if (shouldValidateNow && rule.validator) {
          try {
            await rule.validator(null, normalizedValue);
          } catch (errorMsg) {
            setError(true);
            setErrorMessage(errorMsg);
            onValidationChange && onValidationChange(false, errorMsg);
            onErrorChange && onErrorChange(title, errorMsg);
            return false;
          }
        }

        // Pattern validation
        if (
          shouldValidateNow &&
          rule.pattern &&
          normalizedValue &&
          !rule.pattern.test(normalizedValue)
        ) {
          setError(true);
          setErrorMessage(rule.message || 'Invalid format');
          if (onError) {
            onError({
              field: title || name, // Use title or name as the field identifier
              hasError: error,
              message: errorMessage,
            });
          }
          onValidationChange && onValidationChange(false, rule.message);
          onErrorChange && onErrorChange(title, rule.message);
          return false;
        }

        // Min/Max length validation
        if (
          shouldValidateNow &&
          rule.min &&
          normalizedValue &&
          normalizedValue.length < rule.min
        ) {
          setError(true);
          setErrorMessage(
            rule.message || `Minimum ${rule.min} characters required`
          );
          onValidationChange && onValidationChange(false, rule.message);
          onErrorChange && onErrorChange(title, rule.message);
          return false;
        }

        if (
          shouldValidateNow &&
          rule.max &&
          normalizedValue &&
          normalizedValue.length > rule.max
        ) {
          setError(true);
          setErrorMessage(
            rule.message || `Maximum ${rule.max} characters allowed`
          );
          onValidationChange && onValidationChange(false, rule.message);
          onErrorChange && onErrorChange(title, rule.message);
          return false;
        }
      }

      // No errors
      setError(false);
      setErrorMessage('');
      onValidationChange && onValidationChange(true, '');
      onErrorChange && onErrorChange(title, '');
      return true;
    };

    // Handle change
    const handleChange = (e) => {
      const input = e.target;
      let value = input.value ?? '';
      const selectionStart = input.selectionStart;
      const selectionEnd = input.selectionEnd;

      // 1. Apply auto-trimming if enabled - only prevent leading spaces
      if ($autoTrim && typeof value === 'string' && value.startsWith(' ')) {
        // If the value starts with space, remove the leading space
        value = value.replace(/^\s+/, '');
        // If we modified the value, update the input and return early
        if (value !== input.value) {
          input.value = value;
          onChange(value);
          // Adjust cursor position
          setTimeout(() => {
            input.selectionStart = Math.max(0, selectionStart - 1);
            input.selectionEnd = Math.max(0, selectionEnd - 1);
          }, 0);
          return;
        }
      }

      // 2. ZIP code format
      if (format === 'zip') {
        if (e.nativeEvent.inputType === 'deleteContentBackward') {
          if (value.endsWith('-') && value.length > 1) {
            value = value.slice(0, -2) + value.slice(-1);
          }
        }

        const digitsOnly = value.replace(/\D/g, '');
        value = formatZipCode(digitsOnly);
      }

      // 3. MemberId format
      if (format === 'memberId') {
        value = value.toUpperCase();
      }

      const valueToSend = value;
      onChange(valueToSend);

      // 7. Validation
      if (componentValidateOnChange || anyRuleValidateOnChange) {
        validateValue(value, 'onChange');
      }
    };

    // Validate when isSubmitted changes
    useEffect(() => {
      if (isSubmitted) {
        validateValue(value, 'onSubmit');
      }
    }, [isSubmitted, value]);

    // In Input.jsx
    useEffect(() => {
      const errorInfo = {
        label: title || name,
        message: errorMessage ? errorMessage : null,
      };

      onError && onError(errorInfo);
    }, [errorMessage]);

    return (
      <InputWrapper
        className={className}
        style={{
          ...props.style,
        }}
      >
        <InputRow
          gapLabelInput={gapLabelInput}
          width={widthInput}
          className={classNameRow}
        >
          {label && (
            <InputLabel
              $isPopup={isPopup}
              htmlFor={inputId}
              $widthLabel={widthLabel}
              $maxWidthLabel={$maxWidthLabel}
            >
              <div
                style={subLabel ? { marginTop: '-10px' } : {}}
                className={classNameLabel}
              >
                {label}
                {required && <span className="required">*</span>}
              </div>
              {subLabel && (
                <span
                  style={{ fontSize: '10px', color: '#000', marginTop: '-2px' }}
                >
                  {subLabel}
                </span>
              )}
            </InputLabel>
          )}
          <InputContainer>
            <StyledInput
              autoComplete={autoComplete}
              ref={ref}
              id={inputId}
              $isPopup={isPopup}
              name={props.name || safeTitle || inputId}
              type={props.type}
              placeholder={placeholder}
              value={value}
              onChange={handleChange}
              disabled={props.disabled}
              className={`${error ? 'error' : ''} ${classNameInput}`}
              width={widthInput}
              height={heightInput}
              style={{
                ...props.style,
              }}
              {...props}
            />
            {defaultText && <DefaultText>{defaultText}</DefaultText>}
          </InputContainer>
        </InputRow>
        <ErrorMessage
          $widthError={widthError}
          $paddingMessage={$paddingMessage}
          $show={(error && errorMessage) || errorMessage === null}
        >
          {errorMessage}
        </ErrorMessage>
      </InputWrapper>
    );
  }
);

Input.displayName = 'Input';

Input.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  subLabel: PropTypes.string,
  widthError: PropTypes.number,
  rules: PropTypes.arrayOf(
    PropTypes.shape({
      required: PropTypes.bool,
      message: PropTypes.string,
      validator: PropTypes.func,
      pattern: PropTypes.instanceOf(RegExp),
      min: PropTypes.number,
      max: PropTypes.number,
      validateTrigger: PropTypes.oneOfType([
        PropTypes.oneOf(['onChange']),
        PropTypes.arrayOf(PropTypes.oneOf(['onChange'])),
      ]),
    })
  ),
  normalize: PropTypes.func,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onErrorChange: PropTypes.func,
  widthInput: PropTypes.number,
  hetghtInput: PropTypes.number,
  widthLabel: PropTypes.number,
  disabled: PropTypes.bool,
  onError: PropTypes.func,
  type: PropTypes.string,
  id: PropTypes.string,
  classNameLabel: PropTypes.string,
  defaultText: PropTypes.string,
  isPopup: PropTypes.bool,
  classNameRow: PropTypes.string,
  autoComplete: PropTypes.string,
  className: PropTypes.string,
  classNameInput: PropTypes.string,
  style: PropTypes.object,
  onValidationChange: PropTypes.func,
  validateTrigger: PropTypes.oneOfType([
    PropTypes.oneOf(['onChange']),
    PropTypes.arrayOf(PropTypes.oneOf(['onChange'])),
  ]),
  responsive: PropTypes.bool, // New prop type for responsive behavior
};

export default Input;
